import { Fragment, useRef, useState, type ReactNode } from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy from '@/i18n/ns/storefront';
import { WorkspaceIcon } from '../icons';
import { useStorefrontApi, type StoreDetail } from './api';
import { creditsPhrase, when } from './format';
import { CompanyRequestError } from '@/company/api';
import { plainReason, refusalFacts, refusedChecks, type DesignFailure } from './design-refusal';
import { failureText } from './use-storefront';

export function InstallTab({
  store,
  onChanged,
  notify,
  onFix,
}: {
  store: StoreDetail;
  onChanged: (store: StoreDetail) => void;
  notify: (text: string) => void;
  /** Starts a chat that fixes the design, with the request already written. */
  onFix?: (request: string) => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const locale = useAppCatalogLocale() ?? 'en';
  const api = useStorefrontApi();
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Taking the assistant off the site asks first, like putting it there.
  const [unpublishing, setUnpublishing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  // The design checks that refused the last publish, until the next try.
  const [refusal, setRefusal] = useState<DesignFailure[] | null>(null);
  const [samplesBlocked, setSamplesBlocked] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const unpublishOpener = useRef<HTMLButtonElement>(null);
  const cancelConfirm = () => {
    setConfirming(false);
    requestAnimationFrame(() => opener.current?.focus());
  };
  const version = store.draft?.version ?? store.live?.version ?? 1;
  const newer =
    store.live && store.draft && store.draft.version > store.live.version ? store.draft : null;
  const copyLine = async () => {
    try {
      await navigator.clipboard.writeText(store.install.snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      notify(copy.copyFailed);
    }
  };
  const act = async (action: 'publish' | 'unpublish') => {
    setBusy(true);
    setRefusal(null);
    setSamplesBlocked(false);
    try {
      const { store: next } =
        action === 'publish'
          ? await api.publish(store.id, store.draft?.version ?? null)
          : await api.unpublish(store.id);
      setConfirming(false);
      setUnpublishing(false);
      onChanged(next);
    } catch (failure) {
      // A version that failed the design checks stays on the page with what failed and a way to
      // fix it, instead of a toast that says to try again.
      if (failure instanceof CompanyRequestError && failure.code === 'design_checks_failed') {
        setConfirming(false);
        setRefusal(refusedChecks(failure.detail));
      } else if (
        failure instanceof CompanyRequestError &&
        failure.code === 'design_samples_unavailable'
      ) {
        // Nothing to fix in the design: the site blocks the visit that would prove its card layout.
        setConfirming(false);
        setSamplesBlocked(true);
      } else notify(failureText(failure, copy, copy.actionFailed));
    } finally {
      setBusy(false);
    }
  };
  const lineSeen = store.install.domains.some((domain) => domain.lineFound);
  // Where shoppers meet the published version: only a domain whose page has the line.
  const liveHost = store.install.domains.find((domain) => domain.lineFound)?.host ?? null;
  // The line is looked for on the store's own page; Check again looks now rather than waiting.
  const checkAgain = async () => {
    setChecking(true);
    try {
      onChanged((await api.checkInstall(store.id)).store);
    } catch (failure) {
      notify(failureText(failure, copy, copy.actionFailed));
    } finally {
      setChecking(false);
    }
  };
  // The shop's own security policy: exact steps when reading captured one that refuses the
  // assistant, nothing when it lets the assistant in, the general line when none was captured.
  // Origins and directive names stay whole and copyable.
  const policy = store.install.policy;
  const origin = store.assistantOrigin ?? '';
  const refused = Boolean(policy && (policy.refuses.length || policy.needsEval));
  const policyNote = !origin
    ? null
    : !policy
      ? withCode(copy.installPolicy, {
          origin,
          directives: ['script-src', 'connect-src', 'frame-src'],
        })
      : refused
        ? [
            policy.refuses.length
              ? withCode(copy.installPolicyRefuses, { origin, directives: policy.refuses })
              : null,
            policy.strictDynamic ? copy.installPolicyNonce : null,
            policy.needsEval ? withCode(copy.installPolicyEval, {}) : null,
          ]
            .filter(Boolean)
            .map((part, index) => (
              <Fragment key={index}>
                {index ? ' ' : ''}
                {part}
              </Fragment>
            ))
        : null;
  return (
    <div className="sf-install">
      <section className="sf-step-card" data-done={lineSeen || undefined}>
        <span className="sf-num" aria-hidden="true">
          {lineSeen ? <WorkspaceIcon name="check" size={13} /> : 1}
        </span>
        <div>
          <h2>{copy.installLineTitle}</h2>
          <p>
            {store.live
              ? formatCopy(copy.installLineBodyLive, { version: String(store.live.version) })
              : copy.installLineBody}
          </p>
          <div className="sf-code">
            <code>{store.install.snippet}</code>
            <button className="ws-button" data-testid="sf-copy" onClick={copyLine}>
              <WorkspaceIcon name={copied ? 'check' : 'copy'} size={14} />
              {copied ? copy.copied : copy.copy}
            </button>
          </div>
          {/* A policy that refuses the assistant is said outright; the general advice waits behind
              a question for the sites that have one. */}
          {policyNote &&
            (refused ? (
              <p className="sf-install-policy" data-testid="sf-install-policy" data-refused>
                {policyNote}
              </p>
            ) : (
              <details className="sf-install-policy-ask">
                <summary>{copy.installPolicyAsk}</summary>
                <p className="sf-install-policy" data-testid="sf-install-policy">
                  {policyNote}
                </p>
              </details>
            ))}
        </div>
      </section>

      <section className="sf-step-card" data-done={lineSeen || undefined}>
        <span className="sf-num" aria-hidden="true">
          {lineSeen ? <WorkspaceIcon name="check" size={13} /> : 2}
        </span>
        <div>
          <h2>{copy.domainsTitle}</h2>
          <p>{copy.domainsBody}</p>
          <ul className="sf-domains">
            {store.install.domains.map((domain) => (
              <li key={domain.host} data-found={domain.lineFound || undefined}>
                <WorkspaceIcon name={domain.lineFound ? 'check' : 'clock'} size={14} />
                <span>{domain.host}</span>
                <small data-testid="sf-domain-state">
                  {domain.unverified
                    ? copy.lineUnverified
                    : domain.lineFound
                      ? copy.lineFound
                      : copy.lineNotSeen}
                  {domain.checkedAt &&
                    ` · ${formatCopy(copy.lineChecked, { when: when(domain.checkedAt, locale) })}`}
                </small>
              </li>
            ))}
          </ul>
          {/* The line is looked for once there is a version to find; before that, nothing to check. */}
          {(store.draft || store.live) && (
            <button
              className="ws-button sf-check-line"
              data-testid="sf-check-line"
              disabled={checking}
              onClick={checkAgain}
            >
              {checking ? copy.lineChecking : copy.lineCheckAgain}
            </button>
          )}
        </div>
      </section>

      <section className="sf-step-card" data-done={store.live ? true : undefined}>
        <span className="sf-num" aria-hidden="true">
          {store.live ? <WorkspaceIcon name="check" size={13} /> : 3}
        </span>
        <div>
          {store.live ? (
            <>
              {/* A newer version waiting is the next step; what is published sits under it. */}
              {newer ? (
                <div data-testid="sf-newer">
                  <h2>{formatCopy(copy.newVersionTitle, { version: String(newer.version) })}</h2>
                  <p>
                    {liveHost
                      ? formatCopy(copy.newVersionBodyLive, {
                          live: String(store.live.version),
                          domain: liveHost,
                        })
                      : formatCopy(copy.newVersionBody, { live: String(store.live.version) })}
                  </p>
                  {/* Replacing what shoppers see takes the same confirming tap as the first publish. */}
                  {confirming ? (
                    <div className="sf-confirm" role="group" aria-label={copy.publishTitle}>
                      <b>
                        {formatCopy(copy.publishConfirm, {
                          version: String(newer.version),
                          name: store.name,
                        })}
                      </b>
                      <div>
                        <button
                          className="ws-button primary"
                          data-testid="sf-publish-now"
                          disabled={busy}
                          onClick={() => act('publish')}
                          autoFocus
                        >
                          {busy ? copy.publishing : copy.publishNow}
                        </button>
                        <button className="ws-button" disabled={busy} onClick={cancelConfirm}>
                          {copy.notNow}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      ref={opener}
                      className="ws-button primary"
                      data-testid="sf-publish-newer"
                      disabled={busy}
                      onClick={() => setConfirming(true)}
                    >
                      {formatCopy(copy.publishButton, { version: String(newer.version) })}
                    </button>
                  )}
                </div>
              ) : (
                <h2>{formatCopy(copy.liveTitle, { version: String(store.live.version) })}</h2>
              )}
              <div className="sf-published" data-testid="sf-published">
                <span>
                  {newer
                    ? formatCopy(copy.publishedLine, {
                        version: String(store.live.version),
                        since: when(store.live.since, locale),
                      })
                    : formatCopy(copy.liveSince, { since: when(store.live.since, locale) })}
                </span>
                {!unpublishing && (
                  <button
                    ref={unpublishOpener}
                    className="ws-text-button"
                    data-testid="sf-unpublish"
                    disabled={busy}
                    onClick={() => setUnpublishing(true)}
                  >
                    {copy.unpublish}
                  </button>
                )}
              </div>
              {unpublishing && (
                <div
                  className="sf-confirm"
                  role="group"
                  aria-label={copy.unpublish}
                  data-testid="sf-unpublish-confirm"
                >
                  <b>
                    {formatCopy(copy.unpublishConfirm, { version: String(store.live.version) })}
                  </b>
                  <div>
                    <button
                      className="ws-button"
                      data-testid="sf-unpublish-now"
                      disabled={busy}
                      onClick={() => act('unpublish')}
                      autoFocus
                    >
                      {copy.unpublish}
                    </button>
                    <button
                      className="ws-button"
                      disabled={busy}
                      onClick={() => {
                        // Not now returns focus to Unpublish, as Publish's does to Publish.
                        setUnpublishing(false);
                        requestAnimationFrame(() => unpublishOpener.current?.focus());
                      }}
                    >
                      {copy.notNow}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <h2>{copy.publishTitle}</h2>
              <p>{formatCopy(copy.publishBody, { domain: store.domain })}</p>
              {confirming ? (
                <div
                  className="sf-confirm"
                  role="group"
                  aria-label={copy.publishTitle}
                  data-testid="sf-publish-confirm"
                >
                  <b>
                    {formatCopy(copy.publishConfirm, {
                      version: String(version),
                      name: store.name,
                    })}
                  </b>
                  <div>
                    <button
                      className="ws-button primary"
                      data-testid="sf-publish-now"
                      disabled={busy}
                      onClick={() => act('publish')}
                      autoFocus
                    >
                      {busy ? copy.publishing : copy.publishNow}
                    </button>
                    <button className="ws-button" disabled={busy} onClick={cancelConfirm}>
                      {copy.notNow}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  ref={opener}
                  className="ws-button primary"
                  data-testid="sf-publish"
                  disabled={store.status !== 'ready'}
                  onClick={() => setConfirming(true)}
                >
                  {formatCopy(copy.publishButton, { version: String(version) })}
                </button>
              )}
            </>
          )}
          {samplesBlocked && (
            <div className="sf-refusal" role="alert" data-testid="sf-design-samples-blocked">
              <span>{copy.designSamplesBlocked}</span>
            </div>
          )}
          {refusal && (
            <div className="sf-refusal" role="alert" data-testid="sf-design-refused">
              <b>{copy.errorDesignChecks}</b>
              {plainReason(refusal, copy) && <span>{plainReason(refusal, copy)}</span>}
              {onFix && (
                <button
                  className="ws-button quiet"
                  data-testid="sf-design-fix"
                  onClick={() =>
                    onFix(
                      formatCopy(copy.fixDesignPrompt, {
                        name: store.name,
                        domain: store.domain,
                        facts: refusalFacts(refusal),
                      }),
                    )
                  }
                >
                  <WorkspaceIcon name="chat" size={14} />
                  {copy.fixWithChat}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="sf-usage">
        <h2>{copy.usageTitle}</h2>
        <p>{copy.usageBody}</p>
        <dl>
          <div>
            <dt>{copy.usageDiscovery}</dt>
            <dd>
              {store.usage.discoveryCreditMicros === null ||
              store.usage.discoveryCreditMicros === undefined
                ? copy.costCounting
                : creditsPhrase(store.usage.discoveryCreditMicros, locale, copy)}
            </dd>
          </div>
          <div>
            <dt>{copy.usageChats}</dt>
            <dd>
              {formatCopy(copy.usageChatsValue, {
                count: new Intl.NumberFormat(locale).format(store.usage.chats),
                cost: creditsPhrase(store.usage.chatsCreditMicros, locale, copy),
              })}
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

// A sentence with {origin} and {directives} filled in as code that does not break across lines,
// and anything quoted ('unsafe-eval') as code too, so what the person copies is exact.
function withCode(text: string, values: { origin?: string; directives?: string[] }): ReactNode {
  const parts: ReactNode[] = [];
  text.split(/(\{origin\}|\{directives\}|'[a-z-]+')/).forEach((piece, index) => {
    if (piece === '{origin}')
      parts.push(
        <code key={index} className="sf-policy-code">
          {values.origin}
        </code>,
      );
    else if (piece === '{directives}')
      (values.directives ?? []).forEach((name, at) =>
        parts.push(
          <Fragment key={`${index}-${name}`}>
            {at ? ', ' : ''}
            <code className="sf-policy-code">{name}</code>
          </Fragment>,
        ),
      );
    else if (/^'[a-z-]+'$/.test(piece))
      parts.push(
        <code key={index} className="sf-policy-code">
          {piece}
        </code>,
      );
    else if (piece) parts.push(<Fragment key={index}>{piece}</Fragment>);
  });
  return parts;
}
